import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import '../../styles/SchoolSettings.css';
import {useToast} from "../commons/toast/ToastContext";

const SchoolSettings = ({ schoolId }) => {
    const { t } = useTranslation(['school', 'common']);
    const [settings, setSettings] = useState(null);
    const [formValues, setFormValues] = useState({
        minBookingDaysInAdvance: '',
        minDaysBeforeCancel: '',
        allowMultipleBookings: false,
        allowReserveList: false,
    });
    const [monthSelection, setMonthSelection] = useState([]); // Array of strings representing unlocked months
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('jwt');
    const toast = useToast();

    // Define the 12 months as per Java Month enum
    const monthOptions = [
        { key: "JANUARY", label: t('settings.months.january') },
        { key: "FEBRUARY", label: t('settings.months.february') },
        { key: "MARCH", label: t('settings.months.march') },
        { key: "APRIL", label: t('settings.months.april') },
        { key: "MAY", label: t('settings.months.may') },
        { key: "JUNE", label: t('settings.months.june') },
        { key: "JULY", label: t('settings.months.july') },
        { key: "AUGUST", label: t('settings.months.august') },
        { key: "SEPTEMBER", label: t('settings.months.september') },
        { key: "OCTOBER", label: t('settings.months.october') },
        { key: "NOVEMBER", label: t('settings.months.november') },
        { key: "DECEMBER", label: t('settings.months.december') }
    ];

    // Fetch current school settings on mount (or when schoolId changes)
    useEffect(() => {
        if (!schoolId) return;
        setLoading(true);
        axios.get(`/api/v1/school/${schoolId}/settings`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => {
                const data = res.data;
                setSettings(data);
                setFormValues({
                    minBookingDaysInAdvance: data.minBookingDaysInAdvance || '',
                    minDaysBeforeCancel: data.minDaysBeforeCancel || '',
                    allowMultipleBookings: data.allowMultipleBookings || false,
                    allowReserveList: data.allowReserveList || false,
                });
                // unlockedMonths is a set; convert to array
                setMonthSelection(data.unlockedMonths ? Array.from(data.unlockedMonths) : []);
                setLoading(false);
            })
            .catch((error) => {
                toast.error(t('settings.fetchError'), t('settings.fetchErrorTitle'));
                setLoading(false);
            });
    }, [schoolId, token]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleMonthToggle = (month) => {
        setMonthSelection((prev) => {
            if (prev.includes(month)) {
                return prev.filter((m) => m !== month);
            } else {
                return [...prev, month];
            }
        });
    };

    // Save general school settings (without months)
    const handleSaveSettings = () => {
        setSaving(true);
        const payload = {
            minBookingDaysInAdvance: Number(formValues.minBookingDaysInAdvance),
            minDaysBeforeCancel: Number(formValues.minDaysBeforeCancel),
            allowMultipleBookings: formValues.allowMultipleBookings,
            allowReserveList: formValues.allowReserveList,
        };
        axios.patch(`/api/v1/school/${schoolId}/settings`, payload, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(() => {
                toast.success(t('settings.saveSuccess'), t('settings.saved'));
                setSaving(false);
            })
            .catch((error) => {
                toast.error(t('settings.saveError'), t('settings.saveErrorTitle'));
                setSaving(false);
            });
    };

    // Unlock all months
    const handleUnlockAll = () => {
        setSaving(true);
        axios.patch(`/api/v1/school/${schoolId}/settings/unlock-all`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(() => {
                toast.success(t('settings.allMonthsUnlocked'), t('common:success'));
                setMonthSelection(monthOptions.map(m => m.key));
                setSaving(false);
            })
            .catch((error) => {
                toast.error(t('settings.unlockAllError'), t('common:error'));
                setSaving(false);
            });
    };

    // Lock all months
    const handleLockAll = () => {
        setSaving(true);
        axios.patch(`/api/v1/school/${schoolId}/settings/lock-all`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(() => {
                toast.success(t('settings.allMonthsLocked'), t('common:success'));
                setMonthSelection([]);
                setSaving(false);
            })
            .catch((error) => {
                toast.error(t('settings.lockAllError'), t('common:error'));
                setSaving(false);
            });
    };

    // Apply changes to unlocked months
    const handleApplyMonthSettings = () => {
        setSaving(true);
        const currentUnlocked = settings?.unlockedMonths ? Array.from(settings.unlockedMonths) : [];
        const toUnlock = monthSelection.filter(m => !currentUnlocked.includes(m));
        const toLock = currentUnlocked.filter(m => !monthSelection.includes(m));

        const requests = [];
        if (toUnlock.length > 0) {
            requests.push(
                axios.patch(`/api/v1/school/${schoolId}/settings/unlock`, toUnlock, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            );
        }
        if (toLock.length > 0) {
            requests.push(
                axios.patch(`/api/v1/school/${schoolId}/settings/lock`, toLock, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            );
        }
        if (requests.length === 0) {
            toast.info(t('settings.noMonthChanges'), t('common:info'));
            setSaving(false);
            return;
        }
        Promise.all(requests)
            .then(() => {
                toast.success(t('settings.monthsUpdated'), t('settings.saved'));
                setSaving(false);
            })
            .catch((error) => {
                toast.error(t('settings.monthsUpdateError'), t('common:error'));
                setSaving(false);
            });
    };

    if (loading) return <div className="loading">{t('settings.loading')}</div>;
    if (!settings) return <div className="error-message">{t('settings.loadFailed')}</div>;

    return (
        <div className="school-settings-container">
            {/* General Settings Section */}
            <div className="settings-section settings-section-general">
                {/* Button positioned at top right */}
                <div className="settings-button-group">
                    <button className="settings-btn settings-btn-primary" onClick={handleSaveSettings} disabled={saving}>
                        {saving ? t('common:saving') : t('settings.saveSettings')}
                    </button>
                </div>

                <div className="settings-form-row">
                    <div className="settings-input-group">
                        <label>{t('settings.bookingDaysInAdvance')}</label>
                        <input
                            type="number"
                            name="minBookingDaysInAdvance"
                            value={formValues.minBookingDaysInAdvance}
                            onChange={handleInputChange}
                            className="settings-form-input"
                            placeholder={t('settings.exampleDays', { count: 7 })}
                        />
                    </div>
                    <div className="settings-input-group">
                        <label>{t('settings.cancelDaysBefore')}</label>
                        <input
                            type="number"
                            name="minDaysBeforeCancel"
                            value={formValues.minDaysBeforeCancel}
                            onChange={handleInputChange}
                            className="settings-form-input"
                            placeholder={t('settings.exampleDays', { count: 1 })}
                        />
                    </div>
                </div>

                {/* Side-by-side checkboxes */}
                <div className="settings-checkbox-container">
                    <div className="settings-checkbox-group">
                        <label className="settings-checkbox-label">
                            <input
                                type="checkbox"
                                name="allowMultipleBookings"
                                checked={formValues.allowMultipleBookings}
                                onChange={handleInputChange}
                                className="settings-checkbox-input"
                            />
                            <span className="settings-checkbox-text">{t('settings.allowMultipleBookings')}</span>
                            <span className="settings-tooltip-wrapper">
                                <span className="settings-tooltip-icon">?</span>
                                <span className="settings-tooltip-content">
                                    {t('settings.allowMultipleBookingsTooltip')}
                                </span>
                            </span>
                        </label>
                    </div>

                    <div className="settings-checkbox-group">
                        <label className="settings-checkbox-label">
                            <input
                                type="checkbox"
                                name="allowReserveList"
                                checked={formValues.allowReserveList}
                                onChange={handleInputChange}
                                className="settings-checkbox-input"
                            />
                            <span className="settings-checkbox-text">{t('settings.allowReserveList')}</span>
                            <span className="settings-tooltip-wrapper">
                                <span className="settings-tooltip-icon">?</span>
                                <span className="settings-tooltip-content">
                                    {t('settings.allowReserveListTooltip')}
                                </span>
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Months Settings Section */}
            <div className="settings-section-title">{t('settings.monthSettingsTitle')}</div>

            <div className="settings-section settings-section-months">
                <p className="settings-info-text" style={{ color: '#666', fontSize: '14px', lineHeight: '1.5', marginBottom: '16px', padding: '12px', backgroundColor: '#f5f4ff', borderRadius: '8px', borderLeft: '4px solid #7269FF' }}>
                    {t('settings.monthSettingsDescription')}
                </p>
                <div className="settings-months-grid">
                    {monthOptions.map((month) => (
                        <label key={month.key} className="settings-month-checkbox">
                            <input
                                type="checkbox"
                                checked={monthSelection.includes(month.key)}
                                onChange={() => handleMonthToggle(month.key)}
                                className="settings-month-checkbox-input"
                            />
                            <span className="settings-month-checkbox-text">{month.label}</span>
                        </label>
                    ))}
                </div>

                <div className="settings-button-group">
                    <button className="settings-btn settings-btn-primary" onClick={handleApplyMonthSettings} disabled={saving}>
                        {saving ? t('common:saving') : t('settings.saveMonthSettings')}
                    </button>

                    <div className="settings-months-toggle-group">
                        <label className="settings-months-toggle-label">
                            <input
                                type="checkbox"
                                checked={monthSelection.length === monthOptions.length}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        handleUnlockAll();
                                    }
                                }}
                                className="settings-months-toggle-input"
                                disabled={saving}
                            />
                            <span className="settings-months-toggle-text">{t('settings.unlockAll')}</span>
                        </label>
                    </div>

                    <div className="settings-months-toggle-group">
                        <label className="settings-months-toggle-label">
                            <input
                                type="checkbox"
                                checked={monthSelection.length === 0}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        handleLockAll();
                                    }
                                }}
                                className="settings-months-toggle-input"
                                disabled={saving}
                            />
                            <span className="settings-months-toggle-text">{t('settings.lockAll')}</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchoolSettings;
